import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";  // Keep Firestore import
import Keycloak from "keycloak-js";

// Firebase config for Firestore only (remove auth-related config)
const firebaseConfig = {
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  // Only keeping what's needed for Firestore
};

// Initialize Firebase for Firestore
const app = initializeApp(firebaseConfig);
export const firestore = getFirestore(app);  // Keep Firestore instance

// Resolve Keycloak config with runtime-friendly env fallbacks
const getEnv = (key) => {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key] !== undefined) {
    return import.meta.env[key];
  }
  if (typeof process !== 'undefined' && process.env && process.env[key] !== undefined) {
    return process.env[key];
  }
  return undefined;
};

const inferKeycloakUrl = () => {
  const envUrl =
    getEnv('REACT_APP_KEYCLOAK_URL') ||
    getEnv('REACT_APP_KEYCLOAK_SERVER_URL') ||
    getEnv('KEYCLOAK_URL') ||
    getEnv('KEYCLOAK_SERVER_URL');

  if (envUrl) {
    return envUrl;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.includes('stage') && host.includes('interpretos.com')) {
      return 'https://auth-stage.interpretos.com/';
    }
    if (host.includes('stage') && host.includes('interpretos.ai')) {
      return 'https://auth-stage.interpretos.ai/';
    }
    if (host.includes('interpretos.com')) {
      return 'https://auth.interpretos.com/';
    }
    if (host.includes('interpretos.ai')) {
      return 'https://auth.interpretos.ai/';
    }
  }

  return 'https://auth.interpretos.test/';
};

const keycloakConfig = {
  url: inferKeycloakUrl(),
  realm: getEnv('REACT_APP_KEYCLOAK_REALM') || getEnv('KEYCLOAK_REALM') || 'interpretos',
  clientId: getEnv('REACT_APP_KEYCLOAK_CLIENT_ID') || getEnv('KEYCLOAK_CLIENT_ID') || 'interpretos',
};

console.log('Keycloak config:', keycloakConfig);

export const keycloak = new Keycloak(keycloakConfig);

// Initialize Keycloak - this needs to be called once
let keycloakInitPromise = null;
let keycloakInitialized = false;

const ensureKeycloakReady = async () => {
  if (!keycloakInitPromise) {
    await initializeKeycloak();
  } else {
    await keycloakInitPromise;
  }
};

const initializeKeycloak = async () => {
  // If already initialized, return the existing instance
  if (keycloakInitialized) {
    return keycloak;
  }
  
  // If initialization is in progress, wait for it
  if (keycloakInitPromise) {
    return await keycloakInitPromise;
  }
  
  // Start initialization
  keycloakInitPromise = (async () => {
    try {
      // For development, use simpler configuration to avoid iframe issues
      const initOptions = {
        onLoad: 'check-sso',
        checkLoginIframe: false, // Disable iframe to avoid timeout issues
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        // For development with local Keycloak access issues
        enableLogging: true,
        timeoutInSecond: 10
      };
      
      console.log('Initializing Keycloak with options:', initOptions);
      await keycloak.init(initOptions);
      
      keycloakInitialized = true;
      console.log('Keycloak initialized successfully');
      return keycloak;
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      // Don't reset the promise here to prevent re-initialization attempts
      throw error;
    }
  })();
  
  return await keycloakInitPromise;
};

// Auth state management
let authStateCallbacks = [];

// Mimic Firebase auth state change listener
export const onAuthStateChanged = (callback) => {
  authStateCallbacks.push(callback);
  
  // Call immediately with current state if Keycloak is initialized
  if (keycloakInitPromise) {
    keycloakInitPromise.then(() => {
      callback(getCurrentUser());
    }).catch(() => {
      callback(null);
    });
  } else {
    // If not initialized yet, call with null user
    callback(null);
  }
  
  // Return unsubscribe function
  return () => {
    authStateCallbacks = authStateCallbacks.filter(cb => cb !== callback);
  };
};

// Notify all listeners of auth state changes
const notifyAuthStateChange = (user) => {
  authStateCallbacks.forEach(callback => callback(user));
};

// Get current user in Firebase-compatible format
export const getCurrentUser = () => {
  if (keycloak.authenticated) {
    return {
      uid: keycloak.tokenParsed.sub,
      email: keycloak.tokenParsed.email,
      displayName: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username,
      emailVerified: keycloak.tokenParsed.email_verified || false,
      // Add other properties as needed
    };
  }
  return null;
};

// Authentication functions removed - all login now handled by Keycloak directly via Login component

// signUpWithEmailPassword function removed - registration now handled by Keycloak directly

export const logout = async () => {
  try {
    // Make sure Keycloak is initialised and we have a fresh token before logging
    // out. Without a valid `idToken`, keycloak-js omits `id_token_hint` from the
    // logout request and Keycloak will NOT silently terminate the SSO session,
    // which lets `check-sso` re-authenticate the user on the next page load
    // (symptom: "logout appears to work but you end up logged back in").
    await ensureKeycloakReady();
    try {
      // Force a refresh so `keycloak.idToken` is populated/current.
      await keycloak.updateToken(-1);
    } catch (refreshError) {
      console.warn("Token refresh before logout failed; continuing to log out:", refreshError);
    }

    const logoutOptions = { redirectUri: window.location.origin };
    // Diagnostic: lets us confirm on stage/prod whether `id_token_hint` is present.
    console.log("Keycloak logout:", {
      hasIdToken: Boolean(keycloak.idToken),
      logoutUrl: keycloak.createLogoutUrl(logoutOptions),
    });

    // Do NOT flip app auth state to null before redirecting. Doing so mounts <Login>,
    // which immediately calls keycloak.login() and redirects to the auth endpoint —
    // that redirect then races keycloak.logout() below. If login wins, Keycloak SSOs
    // the still-alive session and the user is instantly logged back in (intermittent,
    // device/timing dependent). keycloak.logout() performs a synchronous top-level
    // redirect, so nothing after it runs anyway; the app re-initialises logged-out
    // when the browser returns from Keycloak.
    await keycloak.logout(logoutOptions);
  } catch (error) {
    console.error("Error during sign-out:", error);
    throw error;
  }
};

export const getToken = async () => {
  try {
    // Ensure Keycloak is initialized before getting token
    await ensureKeycloakReady();
    console.log('Keycloak token request using config', {
      url: keycloakConfig.url,
      realm: keycloakConfig.realm,
      clientId: keycloakConfig.clientId,
      authenticated: keycloak.authenticated,
      tokenIssuer: keycloak.tokenParsed?.iss,
      tokenAud: keycloak.tokenParsed?.aud
    });
    
    if (!keycloak.authenticated) {
    throw new Error("User not authenticated");
  }
    
    // Refresh token if it's about to expire
    await keycloak.updateToken(30);
    return keycloak.token;
  } catch (error) {
    console.error("Error getting authentication token:", error);
    throw error;
  }
};

export const getUserId = async () => {
  try {
    await ensureKeycloakReady();
    if (!keycloak.authenticated) {
      return null;
    }
    return keycloak.tokenParsed?.sub || null;
  } catch (error) {
    console.error('Error retrieving user ID:', error);
    return null;
  }
};

// Setup Keycloak event handlers
const setupKeycloakEvents = async () => {
  await initializeKeycloak();
  
  keycloak.onAuthSuccess = () => {
    console.log('Keycloak auth success');
    notifyAuthStateChange(getCurrentUser());
  };
  
  keycloak.onAuthError = (error) => {
    console.error('Keycloak auth error:', error);
    notifyAuthStateChange(null);
  };
  
  keycloak.onAuthLogout = () => {
    console.log('Keycloak auth logout');
    notifyAuthStateChange(null);
  };
  
  keycloak.onTokenExpired = () => {
    console.log('Keycloak token expired, refreshing...');
    keycloak.updateToken(30).then((refreshed) => {
      if (refreshed) {
        console.log('Token refreshed');
      } else {
        console.log('Token still valid');
      }
    }).catch(() => {
      console.error('Failed to refresh token');
      notifyAuthStateChange(null);
    });
  };
};

// Auto-initialize Keycloak and setup events
setupKeycloakEvents().catch(console.error);

// Export the initialize function for manual initialization if needed
export { initializeKeycloak };
